import { Injectable, OnDestroy } from '@angular/core';
import { isEmpty } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AnalyticsClientRole, AuthStore, DevicesStore } from '@store';

import { environment } from '../../../environments/environment';

import { UserRoleType } from '../../../projects/client';
import { viguideVersion } from '../../../../package-version';
import { GTMEventModel, GTM_CATEGORIES, GTM_EVENTS, GTM_LABELS, GTMEvent } from './gtm';

@Injectable({
  providedIn: 'root',
})
export class GTMService implements OnDestroy {
  environment = { ...environment };
  event: { [key: string]: GTMEvent } = GTM_EVENTS;
  label: { [key: string]: string } = GTM_LABELS;
  category: { [key: string]: string } = GTM_CATEGORIES;
  partnerId: string;
  selectedDeviceId: string;
  selectedGatewayId: string;
  identityType: string;
  analyticsClientGroup: AnalyticsClientRole;
  userIotRole: UserRoleType;

  private ngOnDestroy$ = new Subject<boolean>();

  constructor(private authStore: AuthStore, private devicesStore: DevicesStore) {
    this.authStore
      .getCompanyId()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((companyId) => (this.partnerId = companyId));

    this.authStore
      .getIdentityTypeValue()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((identityTypeValue) => (this.identityType = identityTypeValue));

    this.authStore
      .getUserIotRole()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((userIotRole) => (this.userIotRole = userIotRole));

    this.authStore.analyticsClientGroup$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((analyticsClientGroup) => (this.analyticsClientGroup = analyticsClientGroup));

    this.devicesStore.selectedDevice$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((device) => {
      this.selectedDeviceId = device?.boilerSerial;
      this.selectedGatewayId = device?.gatewaySerial;
    });
  }

  track(eventName: string, actionExtender?: string, category?: string): void {
    const gtmEvent: GTMEvent = this.event[eventName];
    if (!gtmEvent) {
      if (environment.env !== 'production') {
        console.warn(`This gtm tag is missing: ${eventName}`);
      }

      return;
    }

    const actionText = isEmpty(actionExtender)
      ? `${gtmEvent.action}`
      : `${gtmEvent.action}${actionExtender}`;
    const categoryText = isEmpty(category)
      ? gtmEvent.category
      : this.category[category] || category;
    const action = `${actionText} from ${categoryText}`;

    const gtmEventModel: GTMEventModel = {
      event: 'GenericEvent',
      eventCategory: gtmEvent.category,
      eventLabel: gtmEvent.label,
      eventAction: action,
      eventId: gtmEvent.id,
      identityType: this.identityType,
      partnerId: this.partnerId,
      roleGroup: this.userIotRole,
      selectedDeviceId: this.selectedDeviceId,
      selectedGatewayId: this.selectedGatewayId,
      userType: this.analyticsClientGroup,
      viguideVersion,
    };
    window.dataLayer.push(gtmEventModel);
  }

  ngOnDestroy(): void {
    this.ngOnDestroy$.next(true);
    this.ngOnDestroy$.complete();
  }
}
